import { useState } from 'react'
import axios from 'axios'
import imageCompression from 'browser-image-compression'
import { ALLOWED_MIME_TYPES, MAX_FILE_SIZE_BYTES } from '@olimhub/shared'
import type { AllowedMimeType } from '@olimhub/shared'
import { uploadApi } from '@/api'

type UploadState = 'idle' | 'compressing' | 'uploading' | 'done' | 'error'

interface UseUploadResult {
  state: UploadState
  progress: number
  publicUrl: string | null
  error: string | null
  upload: (file: File) => Promise<string | null>
  reset: () => void
}

const POST_IMAGE_OPTIONS = {
  maxSizeMB: 0.8,
  maxWidthOrHeight: 1200,
  useWebWorker: true,
  fileType: 'image/webp' as const,
  initialQuality: 0.85,
}

const AVATAR_OPTIONS = {
  maxSizeMB: 0.3,
  maxWidthOrHeight: 400,
  useWebWorker: true,
  fileType: 'image/webp' as const,
  initialQuality: 0.9,
}

async function compressImage(file: File, type: 'post' | 'avatar'): Promise<File> {
  if (file.type === 'image/gif') return file

  const options = type === 'avatar' ? AVATAR_OPTIONS : POST_IMAGE_OPTIONS

  try {
    const compressed = await imageCompression(file, options)

    if (import.meta.env.DEV) {
      console.log(
        `📸 Image optimized (${type}): ` +
          `${(file.size / 1024 / 1024).toFixed(2)}MB → ` +
          `${(compressed.size / 1024 / 1024).toFixed(2)}MB ` +
          `(${Math.round((1 - compressed.size / file.size) * 100)}% smaller)`,
      )
    }

    return compressed
  } catch (err) {
    console.warn('Compression failed, using original:', err)
    return file
  }
}

export function useUpload(type: 'post' | 'avatar' = 'post'): UseUploadResult {
  const [state, setState] = useState<UploadState>('idle')
  const [progress, setProgress] = useState(0)
  const [publicUrl, setPublicUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setState('idle')
    setProgress(0)
    setPublicUrl(null)
    setError(null)
  }

  const upload = async (file: File): Promise<string | null> => {
    // Validate MIME type on the original file
    if (!(ALLOWED_MIME_TYPES as readonly string[]).includes(file.type)) {
      setError(`File type not allowed. Allowed: ${ALLOWED_MIME_TYPES.join(', ')}`)
      setState('error')
      return null
    }

    setState('compressing')
    setProgress(0)
    setError(null)

    const fileToUpload = await compressImage(file, type)

    // Safety net size check after compression
    if (fileToUpload.size > MAX_FILE_SIZE_BYTES) {
      setError(`File too large. Max size is ${MAX_FILE_SIZE_BYTES / 1024 / 1024}MB`)
      setState('error')
      return null
    }

    setState('uploading')

    try {
      const { uploadUrl, key } = await uploadApi.presign(fileToUpload.type as AllowedMimeType, fileToUpload.size)

      await axios.put(uploadUrl, fileToUpload, {
        headers: { 'Content-Type': fileToUpload.type },
        onUploadProgress: (e) => {
          setProgress(Math.round((e.loaded / (e.total ?? 1)) * 100))
        },
      })

      const { publicUrl: url } = await uploadApi.confirm(key)
      setPublicUrl(url)
      setState('done')
      return url
    } catch (err) {
      console.error('[upload] error', err)
      const message = err instanceof Error ? err.message : 'Upload failed'
      setError(message)
      setState('error')
      return null
    }
  }

  return { state, progress, publicUrl, error, upload, reset }
}
